import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { readSupportSessionId } from '../../../utils/supportSession';

const BAR_HEIGHT = 44;

const Bar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Above the parent navbar (5000) and the incentive tracker (4555), below
     the loading veil (8888) and modals (10000). */
  z-index: 6000;
  height: ${BAR_HEIGHT}px;
  overflow: hidden;
  background-color: #8a1c1c;
  color: #fff;
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const Message = styled.span`
  line-height: 1.3;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Remaining = styled.span`
  font-weight: 400;
  opacity: 0.85;
  flex-shrink: 0;
  @media (max-width: 480px) {
    display: none;
  }
`;

const ExitForm = styled.form`
  margin: 0;
  flex-shrink: 0;
`;

const ExitButton = styled.button`
  background: #fff;
  color: #8a1c1c;
  border: none;
  border-radius: 3px;
  padding: 5px 14px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: #f1f1f1;
  }
`;

interface SupportSessionState {
  active: boolean;
  parentName?: string;
  adminName?: string;
  expiresAt?: string;
  secondsRemaining?: number;
}

const csrfToken = (): string => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') || '' : '';
};

const formatRemaining = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
};

const SupportSessionBanner: FC = () => {
  const supportSession = useSelector(
    (state: { auth: { supportSession?: SupportSessionState | null } }) =>
      state.auth?.supportSession,
  );
  const expiresAt = supportSession?.expiresAt;
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!expiresAt) {
      setRemaining(null);
      return undefined;
    }

    const tick = () => {
      const left = Math.round(
        (new Date(expiresAt).getTime() - Date.now()) / 1000,
      );
      setRemaining(Math.max(left, 0));
    };

    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);

  const active = !!supportSession?.active;

  useEffect(() => {
    // Drives body.is-support-session in definitions.scss, which shifts the navbar,
    // the incentive tracker and every offset that clears them.
    document.body.classList.toggle('is-support-session', active);
    return () => document.body.classList.remove('is-support-session');
  }, [active]);

  if (!active) return null;

  return (
    <Bar role="alert">
      <Message
        title={`You are viewing as ${
          supportSession.parentName || 'this parent'
        }${supportSession.adminName ? ` (${supportSession.adminName})` : ''}`}
      >
        You are viewing as {supportSession.parentName || 'this parent'}
        {supportSession.adminName ? ` (${supportSession.adminName})` : ''}
      </Message>
      {remaining !== null && (
        <Remaining>{formatRemaining(remaining)} left</Remaining>
      )}
      <ExitForm method="post" action="/support-session">
        <input
          type="hidden"
          name="support_session_id"
          value={readSupportSessionId()}
        />
        <input type="hidden" name="_method" value="delete" />
        <input type="hidden" name="authenticity_token" value={csrfToken()} />
        <ExitButton type="submit">Exit</ExitButton>
      </ExitForm>
    </Bar>
  );
};

export default SupportSessionBanner;
